import React, { useEffect, useState } from 'react';
import Select, { ValueType } from 'react-select';
import Modal from '../../../../ui/Model';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { StaffPicker } from '../../../../shared/components/group-ui/staff-picker/staff-picker.component';
import { TeamMemberUiModel, TeamUiModel } from '../../../../shared/ui-models/team.ui-model';

/**
 * Roles as the team service spells them. Deliberately not `UserRole` — that is
 * the authenticator's job title, this is the member's place in the reporting
 * tree, and conflating them would tie a promotion to an access change.
 */
export const TEAM_ROLES = [
  { value: 'manager', label: 'Manager' },
  { value: 'lead', label: 'Team Lead' },
  { value: 'senior', label: 'Senior Agent' },
  { value: 'agent', label: 'Agent' }
];

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';

interface Option {
  value: string;
  label: string;
}

export interface MemberFormValue {
  userId: string;
  role: string;
  team: string[];
  reportsTo?: string;
}

interface MemberFormModalProps {
  show: boolean;
  /** Absent means create. Present means edit, and the person is then fixed. */
  member?: TeamMemberUiModel;
  teams: TeamUiModel[];
  onClose: () => void;
  onSubmit: (value: MemberFormValue) => void;
}

export const MemberFormModal: React.FC<MemberFormModalProps> = ({
  show,
  member,
  teams,
  onClose,
  onSubmit
}) => {
  const isEdit = Boolean(member);
  const [userId, setUserId] = useState<string | undefined>();
  const [role, setRole] = useState('agent');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [reportsTo, setReportsTo] = useState<string | undefined>();

  useEffect(() => {
    setUserId(member?.userId);
    setRole(member?.role ?? 'agent');
    setTeamIds(member?.teamIds ?? []);
    setReportsTo(member?.reportsToId);
  }, [member, show]);

  // The service rejects a non-manager without a manager, and rejects a manager
  // that has one. Mirroring both here turns a 400 into a disabled button.
  const managerRequired = role !== 'manager';
  const canSubmit =
    Boolean(userId) && teamIds.length > 0 && (managerRequired ? Boolean(reportsTo) : true);

  const teamOptions: Option[] = teams.map((team: TeamUiModel) => ({
    value: team.id,
    label: team.name
  }));

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      containerClassName="lg:h-fit lg:max-h-[85vh] lg:w-[560px]"
      headerContent={
        <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
          {isEdit ? 'Edit member' : 'Add member'}
        </div>
      }
      footerContent={
        <div className="flex justify-end gap-4">
          <Button type={ButtonType.gray} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={ButtonType.primary}
            disabled={!canSubmit}
            tracking={{
              event: 'record_saved',
              entity: 'team_member',
              action: isEdit ? 'update' : 'create',
              outcome: 'success'
            }}
            onClick={() =>
              onSubmit({
                userId: userId as string,
                role,
                team: teamIds,
                reportsTo: managerRequired ? reportsTo : undefined
              })
            }
          >
            {isEdit ? 'Save' : 'Add member'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 px-2 py-2">
        <div>
          <label className={LABEL}>Person</label>
          <StaffPicker
            value={userId}
            onChange={setUserId}
            isDisabled={isEdit}
            isClearable={!isEdit}
            placeholder="Search staff by name or email"
          />
          {isEdit ? (
            <p className="mt-1 text-xs text-neutral-400">
              A member is identified by their account — changing it would move the row rather than
              edit it. Archive and re-add instead.
            </p>
          ) : null}
        </div>

        <div>
          <label className={LABEL}>Role</label>
          <Select
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectMenuPortalStyles}
            classNamePrefix="react-select"
            options={TEAM_ROLES}
            value={TEAM_ROLES.find((option) => option.value === role)}
            onChange={(option: ValueType<Option, false>) =>
              setRole(option ? (option as Option).value : 'agent')
            }
          />
        </div>

        <div>
          <label className={LABEL}>Teams</label>
          <Select
            isMulti
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectMenuPortalStyles}
            classNamePrefix="react-select"
            options={teamOptions}
            placeholder={
              teams.length ? 'Select one or more teams' : 'No teams yet — create one first'
            }
            value={teamOptions.filter((option) => teamIds.indexOf(option.value) >= 0)}
            onChange={(value: ValueType<Option, true>) =>
              setTeamIds(((value as Option[]) || []).map((option) => option.value))
            }
          />
        </div>

        <div>
          <label className={LABEL}>
            Reports to {managerRequired ? '' : '— managers report to nobody'}
          </label>
          <StaffPicker
            value={reportsTo}
            onChange={setReportsTo}
            isDisabled={!managerRequired}
            excludeIds={userId ? [userId] : []}
            placeholder="Search their manager"
          />
        </div>
      </div>
    </Modal>
  );
};
